import { useState, type ReactNode } from "react";
import Icon from "@mui/material/Icon";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import AppButton from "../form/AppButton";
import AppIconButton from "../form/AppIconButton";

/** Max visible action buttons on desktop before overflow into “more”. */
const DESKTOP_VISIBLE_MAX = 3;

export type ResponsiveActionItem = {
  key: string;
  label: string;
  icon?: string;
  onClick: () => void;
  /** Stays visible as a button on phones; preferred among the first desktop slots. */
  primary?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  /** Visual style of the desktop button. */
  variant?: "contained" | "outlined" | "text";
  color?: "primary" | "error" | "success" | "warning";
  /** Optional replacement for the desktop button (e.g. a file picker). */
  render?: () => ReactNode;
};

type Props = { items: ResponsiveActionItem[]; moreLabel?: string };

function splitForDesktop(visible: ResponsiveActionItem[]) {
  const primary = visible.filter((item) => item.primary);
  const secondary = visible.filter((item) => !item.primary);
  const ordered = [...primary, ...secondary];
  const shown = ordered.slice(0, DESKTOP_VISIBLE_MAX);
  const overflow = ordered.slice(DESKTOP_VISIBLE_MAX);
  return { shown, overflow };
}

function OverflowMenu({
  items,
  moreLabel,
  anchor,
  setAnchor,
}: {
  items: ResponsiveActionItem[];
  moreLabel?: string;
  anchor: HTMLElement | null;
  setAnchor: (el: HTMLElement | null) => void;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <>
      <AppIconButton
        aria-label={moreLabel ?? t("navbar.more")}
        onClick={(e) => setAnchor(e.currentTarget)}
        busyIgnore
      >
        <Icon sx={{ color: "text.primary" }}>more_vert</Icon>
      </AppIconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.key}
            disabled={item.disabled}
            onClick={() => {
              setAnchor(null);
              item.onClick();
            }}
          >
            {item.icon && (
              <ListItemIcon>
                <Icon fontSize="small" color={item.color === "error" ? "error" : undefined}>
                  {item.icon}
                </Icon>
              </ListItemIcon>
            )}
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Standard page actions: ≤3 visible buttons; the rest go behind “more”.
 * On phones, only `primary` items stay as buttons; everything else is in the menu.
 */
export default function ResponsiveActions({ items, moreLabel }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const visible = items.filter((item) => !item.hidden);

  if (!phone) {
    const { shown, overflow } = splitForDesktop(visible);
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {shown.map((item) =>
          item.render ? (
            <span key={item.key}>{item.render()}</span>
          ) : (
            <AppButton
              key={item.key}
              size="medium"
              variant={item.variant ?? (item.primary ? "contained" : "outlined")}
              color={item.color}
              disabled={item.disabled}
              startIcon={
                item.icon ? <Icon sx={{ fontSize: "1.05rem !important" }}>{item.icon}</Icon> : undefined
              }
              onClick={item.onClick}
            >
              {item.label}
            </AppButton>
          )
        )}
        <OverflowMenu items={overflow} moreLabel={moreLabel} anchor={anchor} setAnchor={setAnchor} />
      </Stack>
    );
  }

  const primary = visible.filter((item) => item.primary);
  const rest = visible.filter((item) => !item.primary);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {primary.map((item) => (
        <AppButton
          key={item.key}
          size="medium"
          variant="contained"
          color={item.color}
          disabled={item.disabled}
          startIcon={
            item.icon ? <Icon sx={{ fontSize: "1.05rem !important" }}>{item.icon}</Icon> : undefined
          }
          onClick={item.onClick}
          sx={{ flex: 1 }}
        >
          {item.label}
        </AppButton>
      ))}
      <OverflowMenu items={rest} moreLabel={moreLabel ?? t("navbar.more")} anchor={anchor} setAnchor={setAnchor} />
    </Stack>
  );
}
